// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import DataTableContext from "@stoneage/MobiusOverrides/DataTable/DataTableContext";
import { fontConfig } from "@stoneage/XDL/constants";
import { ellipsis, xScreen } from "@stoneage/XDL/Utilities";
import { StyledButton } from "@insite/mobius/Clickable";
import DataTableCellBase, { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { injectCss } from "@insite/mobius/utilities";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import * as React from "react";
import styled, { css, withTheme } from "styled-components";
import Button, { ButtonWrapper } from "@insite/mobius/Button";

export interface DataTableCellProps extends DataTableCellBaseProps {
    /** Props to be passed to the cell's typography child. */
    typographyProps?: TypographyPresentationProps;
    /**
     * Indicates how the `css` property is combined with the default `css` property from the theme.
     * If true, the default css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the default css in the theme.
     */
    mergeCss?: boolean;
    // XNG-Changes
    header?: string;
}

// XNG-Changes
const HeaderTypography = styled.p`
    flex: 0 0 calc(50% - 6px);
    font-size: ${fontConfig.smallSingleLine.size}px;
    line-height: 19px;
    font-weight: ${fontConfig.weight.medium};
    white-space: pre-wrap;
    text-align: left;
    ${xScreen("lte", "sm")} {
        font-size: ${fontConfig.smallSingleLine.size}px;
        line-height: ${fontConfig.smallSingleLine.lineHeight};
    }
    &:empty {
        display: none;
    }

    & + div {
        flex: 0 0 calc(50% - 6px);
    }
`;

// XNG-Changes
const MobileDataTableCellBase = styled.div`
    ${injectCss}
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    column-gap: 12px;
    > ${HeaderTypography} {
        font-size: ${fontConfig.smallSingleLine.size}px;
        line-height: ${fontConfig.smallSingleLine.lineHeight};
        font-weight: ${fontConfig.weight.medium};
        text-align: left;
    }
    > ${StyledButton} {
        display: block;
        align-items: flex-start;
        -webkit-box-align: start;
        width: 100%;
        ${ellipsis(2)}
        ${TypographyStyle} {
            font-size: ${fontConfig.smallSingleLine.size}px;
            line-height: ${fontConfig.smallSingleLine.lineHeight};
            font-weight: ${fontConfig.weight.medium};
            text-align: left;
        }
    }
    ${TypographyStyle} {
        font-size: ${fontConfig.smallSingleLine.size}px;
        line-height: ${fontConfig.smallSingleLine.lineHeight};
        text-align: left;
    }
    & > ${ButtonWrapper} {
        margin-right: auto;
    }
    padding: 0;
    max-width: unset;
    width: 100%;
`;

// XNG-Changes
export const ValueTypography = styled.p`
    flex: 0 0 calc(50% - 6px);
    font-size: ${fontConfig.smallSingleLine.size}px;
    line-height: ${fontConfig.smallSingleLine.lineHeight};
    font-weight: ${fontConfig.weight.regular};
    text-align: left;
    white-space: pre-wrap;
    overflow: hidden;
    &:empty:after {
        content: "- -";
    }
    ${xScreen("lte", "md")} {
        font-size: ${fontConfig.smallSingleLine.size}px;
        line-height: ${fontConfig.smallSingleLine.lineHeight};
        overflow: auto;
        text-overflow: unset;
    }
`;

const DataTableCell: React.FC<DataTableCellProps> = ({
    mergeCss,
    css: cellCss = "",
    children,
    typographyProps,
    ...otherProps
}) => {
    const { applyStyledProp } = applyPropBuilder({ ...otherProps }, { component: "dataTable" });
    const { theme } = otherProps;
    const resolvedMergeCss = mergeCss ?? theme?.dataTable.defaultProps?.mergeCss;
    // XNG-Changes: add MobileDataTableCellBase
    const isPrimitive = (val: unknown): val is string | number => typeof val === "string" || typeof val === "number";
    const shouldWrap = isPrimitive(children) || (Array.isArray(children) && children.every(isPrimitive));
    return (
        <DataTableContext.Consumer>
            {({ cellTypographyProps, isMobile }) =>
                isMobile ? (
                    <MobileDataTableCellBase
                        {...otherProps}
                        css={css`
                            ${applyStyledProp(["cssOverrides", "cell"], resolvedMergeCss)}
                            ${cellCss}
                        `}
                    >
                        <HeaderTypography>{otherProps.header}</HeaderTypography>
                        {shouldWrap ? (
                            <ValueTypography {...cellTypographyProps} {...typographyProps}>
                                {children}
                            </ValueTypography>
                        ) : (
                            children
                        )}
                    </MobileDataTableCellBase>
                ) : (
                    <DataTableCellBase
                        {...otherProps}
                        css={css`
                            ${applyStyledProp(["cssOverrides", "cell"], resolvedMergeCss)}
                            ${cellCss}
                        `}
                    >
                        {typeof children === "string" || typeof children === "number" ? (
                            <Typography {...cellTypographyProps} {...typographyProps}>
                                {children}
                            </Typography>
                        ) : (
                            children
                        )}
                    </DataTableCellBase>
                )
            }
        </DataTableContext.Consumer>
    );
};

export default withTheme(DataTableCell);
